import { CloseCircleFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Card, Modal, Progress, Skeleton, Space } from "antd";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { ResourceGroupUsage } from "models/resourceUsage";
import { localizedDateAndTime } from "util/helpers";

const useResourceUsage = (enabled: boolean) => {
    const getUsage = useAuthenticatedRequest<ResourceGroupUsage[]>({
        method: "get",
        url: "/dashboard/resource-usage"
    });

    const { data = [], isLoading } = useQuery(["dashoard", "resource-usage"], getUsage, {
        enabled
    });

    return { data, isLoading };
}

const DescriptionsByLimit: Record<number, string> = {
    5000: "Consumed when checking for new or ended auctions, active bids, and fetching or ending individual auctions.",
    50000: "Consumed when a card is relisted from the cards table, or from the 9 PM relist job.",
    100000: "Consumed when a pending listing is uploaded to eBay."
};

const ResourceCard = ({ context, used, limit, remaining, resetsOn }: ResourceGroupUsage) => {
    const percent = (used / limit) * 100;
    const full = percent >= 100 ? 'exception' : undefined;

    return <Space direction="vertical" size={15}>
        <p style={{ fontWeight: 'bold' }}>{context}</p>
        <p>{used.toLocaleString()} used, {remaining.toLocaleString()} available</p>
        <Progress
            type="circle"
            percent={(used / limit) * 100}
            format={() => full ? <CloseCircleFilled /> : `${percent.toFixed(1)}%`}
            width={150}
            status={full}
        />
        <p>{DescriptionsByLimit[limit] ?? 'Unknown usage'}</p>
        <p>Usage resets at {localizedDateAndTime(resetsOn)}</p>
    </Space>;
}

const ResourceUsage = ({ open, onClosed }: { open: boolean, onClosed: () => void }) => {
    const { data, isLoading } = useResourceUsage(open);

    return <Modal
        open={open}
        destroyOnClose
        onCancel={onClosed}
        onOk={onClosed}
        cancelButtonProps={{ style: { display: 'none' } }}
        width='1050px'
        closable={false}
    >
        <Card title="eBay API Usage">
            {isLoading && <Skeleton />}

            {data.map(x => (<Card.Grid style={{ textAlign: 'center' }}>
                <ResourceCard {...x} />
            </Card.Grid>))}
        </Card>
    </Modal>
};

export default ResourceUsage;